import { REGIME_BEHAVIOR, REGIME_NEXT, shortDate, signed, useBoxWidth, initials } from "./srUtil";
import { fundRegimeDetail } from "./FundRegimeDetail";
import { RegimeVerdict, RegimeSteps, PhasePill } from "./RegimeVerdict";
const OPTIONS = {
  quiet: ["Quiet", "Little unusual options trading."],
  neutral: ["Normal", "Options trading looks ordinary."],
  hot: ["Heated", "Unusually heavy options trading, often a sign of a crowded trade."],
};

// Stock's path so far vs the regime's typical shape, extended by one month only.
const PatternChart = ({ r }) => {
  const [ref, w] = useBoxWidth(560);
  const W = Math.max(260, w), narrow = W < 480, H = narrow ? 220 : 250, X0 = narrow ? 42 : 48, X1 = W - 16, bot = H - 50;
  const n = r.observed.length, total = n + (r.next ? 1 : 0);
  const vals = [...r.observed, ...r.archetype, ...(r.next ? [r.next.value] : [])];
  const lo = Math.min(0, ...vals), hi = Math.max(1, ...vals);
  const x = (i) => X0 + (i / Math.max(1, total - 1)) * (X1 - X0), y = (a) => bot - ((a - lo) / (hi - lo)) * (bot - 30);
  const line = (arr, k0 = 0) => arr.map((a, i) => `${i ? "L" : "M"}${x(i + k0).toFixed(1)},${y(a).toFixed(1)}`).join(" ");
  const ext = r.next && r.archetype.length ? line([r.archetype[n - 1], r.next.value], n - 1) : null;
  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="The stock's path so far next to the typical shape of this regime, one month ahead" data-testid="sr-pattern-chart">
        {[[lo, "Low"], [hi, "High"]].map(([a, l]) => (
          <g key={l}><line x1={X0} x2={X1} y1={y(a)} y2={y(a)} stroke="#26303b" /><text x="4" y={y(a) + 4} fill="#8e9aa9" fontSize="11">{l}</text></g>
        ))}
        {r.next && <rect x={x(n - 1)} y="22" width={x(n) - x(n - 1)} height={bot - 22} fill="#a0aab8" opacity=".06" />}
        {r.archetype.length > 1 && <path d={line(r.archetype)} stroke="#a0aab8" strokeWidth="2.5" strokeDasharray="5 5" fill="none" />}
        {ext && <path d={ext} stroke="#e6eaf0" strokeWidth="2" strokeDasharray="2 4" fill="none" data-testid="sr-pattern-next-line" />}
        <path d={line(r.observed)} stroke="#38bdf8" strokeWidth="3" fill="none" />
        <circle cx={x(n - 1)} cy={y(r.observed[n - 1])} r="4" fill="#38bdf8" />
        <text x={x(n - 1) - (r.next ? 5 : 0)} y="16" textAnchor="end" fill="#38bdf8" fontSize="11">today</text>
        {r.next && <text x={x(n)} y="16" textAnchor="end" fill="#a0aab8" fontSize="11">{x(n) - x(n - 1) < 62 ? "+1 mo" : "+1 month"}</text>}
        <text x={X0} y={bot + 26} fill="#8e9aa9" fontSize="11">{narrow ? "Funds buy" : "Funds start buying"}</text>
      </svg>
    </div>
  );
};

const FundRegimes = ({ r, d, onDetail }) => {
  const buyers = Object.fromEntries(d.current.buyers.map((b) => [b.cik, b]));
  return (
    <div data-testid="sr-regime-funds">
      <span className="eyebrow">The funds behind it</span>
      <p className="muted fundsintro">The funds with the best track records that hold a meaningful position in {d.symbol}, out of {r.n_funds} tracked, with how their own position has done against the sector. Click a fund to see its position.</p>
      {r.funds.length ? r.funds.map((f) => {
        const last = f.path_pct[f.path_pct.length - 1];
        return (
          <button className="regimefund" key={f.cik} data-testid={`sr-regime-fund-${f.cik}`}
            onClick={() => onDetail(fundRegimeDetail(f, r, d.symbol, buyers[f.cik], d.sector))}>
            <span className="avatar">{initials(f.name)}</span>
            <div><b>{f.name}</b><small>Bought {shortDate(f.bought, true)} · held {f.months} month{f.months === 1 ? "" : "s"}</small></div>
            <span className="fundret" data-testid={`sr-regime-fund-ret-${f.cik}`}>
              <b className={last >= 0 ? "green" : "red"}>{signed(last)}%</b>
              <small>{f.since_buy ? "vs sector" : `vs sector, last ${f.path_pct.length - 1} mo`}</small></span>
          </button>
        );
      }) : <p className="muted" data-testid="sr-regime-funds-empty">None of the tracked funds holds a meaningful position in {d.symbol}.</p>}
    </div>
  );
};

export const RegimeNowcast = ({ d, onDetail }) => {
  const r = d.short.regime, symbol = d.symbol;
  if (!r) {
    return <section className="card pad pattern fadein" id="sr-pattern" data-testid="sr-pattern"><span className="eyebrow">Regime nowcast</span>
      <p className="muted">No regime reading for {symbol} yet.</p></section>;
  }
  const opt = OPTIONS[r.options_state];
  return (
    <section className="card pad pattern fadein" id="sr-pattern" data-testid="sr-pattern">
      <span className="eyebrow">Regime nowcast · {shortDate(r.as_of, true)}</span>
      <RegimeVerdict v={r.verdict} symbol={symbol} />
      <RegimeSteps r={r} symbol={symbol} />
      <h3 data-testid="sr-pattern-name" className="patternname">{r.name} <PhasePill group={r.group} testid="sr-pattern-phase" /></h3>
      <p className="muted">{REGIME_BEHAVIOR[r.cluster]}{r.next ? ` If ${symbol} stays in this regime, the next month usually ${REGIME_NEXT[r.next.direction]}.` : ""}</p>
      <div className="patterngrid">
        <div>
          <PatternChart r={r} />
          <div className="clocklegend"><span className="blue">━━ {symbol} so far</span><span>┄┄ Typical shape of this regime</span>{r.next && <span>··· Next month (typical)</span>}</div>
          <p className="smallnote">Shapes only, not price levels. We look one month ahead at most.</p>
        </div>
        {r.funds ? <FundRegimes r={r} d={d} onDetail={onDetail} /> : <div className="muted">No fund-level readings yet.</div>}
      </div>
      <div className="patternlower" data-testid="sr-pattern-options"><span className="eyebrow">Options activity</span>
        <h4 className={r.options_state === "hot" ? "amber" : ""}>{opt ? opt[0] : "Not tracked"}</h4>
        <p className="muted">{opt ? opt[1] : `We don’t track options for ${symbol}.`}</p></div>
      <p className="smallnote">A near-term read of how the price has moved since the funds bought. For the six-month view, see Long-term outlook.</p>
    </section>
  );
};
